import type { RawSlateUpdatesDto } from '../dtos/raw-slate-updates.dto.js'
import type { RawSlateDto } from '../dtos/raw-slate.dto.js'
import type { SlateRestrictionsDto } from '../dtos/slate-restrictions.dto.js'

import { equals } from './equality.utils.js'

export class SlateRestrictionsError extends Error {
  updates: RawSlateUpdatesDto

  restrictions: SlateRestrictionsDto

  details?: unknown

  constructor(
    restrictions: SlateRestrictionsDto,
    updates: RawSlateUpdatesDto,
    details?: unknown,
  ) {
    super(`The update violates the slate restrictions`)
    this.name = 'SlateRestrictionsError'
    this.updates = updates
    this.restrictions = restrictions
    this.details = details
  }
}

const areChildrenMoved = (
  children: string[],
  newChildren: string[],
): boolean => {
  let cIdx = 0
  let nIdx = 0
  while (cIdx < children.length && nIdx < newChildren.length) {
    if (children[cIdx] === newChildren[nIdx]) {
      cIdx += 1
      nIdx += 1
    } else {
      cIdx += 1
    }
  }
  return nIdx !== newChildren.length
}

export const assertReplacementBlocksExclusive = (
  updates: RawSlateUpdatesDto,
  label?: string,
): void => {
  if (!Array.isArray(updates.replacementBlocks)) {
    return
  }
  const hasUpdated = (updates.updatedBlocks?.length ?? 0) > 0
  const hasAdded = (updates.addedBlocks?.length ?? 0) > 0
  const hasRemoved = (updates.removedBlocks?.length ?? 0) > 0
  if (hasUpdated || hasAdded || hasRemoved) {
    const prefix = label ? `${label}: ` : ''
    throw new Error(
      `${prefix}replacementBlocks cannot be used together with updatedBlocks, addedBlocks or removedBlocks`,
    )
  }
}

export const validateRawSlateUpdates = (
  slate: RawSlateDto,
  updates: RawSlateUpdatesDto,
): void => {
  assertReplacementBlocksExclusive(updates)

  const {
    lockedRootBlock = false,
    nonEditableBlocks = [],
    lockedChildrenBlocks = [],
    nonRemovableBlocks = [],
  } = slate.restrictions || {}
  const {
    rootBlock,
    updatedBlocks = [],
    removedBlocks = [],
    replacementBlocks,
  } = updates
  const blocksByIds = slate.blocks.reduce(
    (preValue, block) => ({ ...preValue, [block.id]: block }),
    {},
  )
  const replacementBlocksById = Array.isArray(replacementBlocks)
    ? replacementBlocks.reduce(
        (preValue, block) => ({ ...preValue, [block.id]: block }),
        {} as Record<string, (typeof replacementBlocks)[number]>,
      )
    : undefined

  const violatesRootBlockRestriction = lockedRootBlock && rootBlock
  const violatesEditBlockRestriction = nonEditableBlocks.find(id => {
    const original = blocksByIds[id]
    if (!original) {
      return false
    }
    if (replacementBlocksById) {
      const block = replacementBlocksById[id]
      return (
        block &&
        (!equals(block.props, original.props) ||
          !equals(block.output, original.output))
      )
    }
    return updatedBlocks.find(updatedBlock => {
      if (updatedBlock.id !== id) {
        return false
      }
      const nextProps =
        updatedBlock.props !== undefined ? updatedBlock.props : original.props
      const nextOutput =
        updatedBlock.output !== undefined
          ? updatedBlock.output
          : original.output
      return (
        !equals(nextProps, original.props) ||
        !equals(nextOutput, original.output)
      )
    })
  })
  const violatesLockedChildrenRestrictions = lockedChildrenBlocks.find(id => {
    if (replacementBlocksById) {
      const block = replacementBlocksById[id]
      const original = blocksByIds[id]
      return (
        block &&
        original &&
        block.children &&
        areChildrenMoved(original.children || [], block.children)
      )
    }
    return updatedBlocks.find(
      updatedBlock =>
        updatedBlock.id === id &&
        updatedBlock.children &&
        areChildrenMoved(
          blocksByIds[id]?.children || [],
          updatedBlock.children,
        ),
    )
  })
  const violatesRemoveBlockRestriction = nonRemovableBlocks.find(id => {
    if (replacementBlocksById) {
      return blocksByIds[id] && !replacementBlocksById[id]
    }
    return removedBlocks.find(removedBlock => removedBlock === id)
  })

  if (
    violatesRootBlockRestriction ||
    violatesEditBlockRestriction ||
    violatesLockedChildrenRestrictions ||
    violatesRemoveBlockRestriction
  ) {
    throw new SlateRestrictionsError(slate.restrictions, updates, {
      violatesRootBlockRestriction,
      violatesEditBlockRestriction,
      violatesLockedChildrenRestrictions,
      violatesRemoveBlockRestriction,
    })
  }
}
